import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  FormControlLabel,
  ClickAwayListener,
  TextField,
  InputAdornment,
} from "@mui/material";
import dayjs from "dayjs";
import ShepCheckbox from "@/components/GenericComponents/ShepCheckbox";
import { FilterType, FilterDropDownType, StatusFilterType } from "@/utils/utils";
import CustomDatePicker from "@/components/CustomDatePicker/CustomDatePicker";
import _ from "lodash";
import { FilterIcon, SearchIcon } from "@/ImageRepo";
import "./CustomFilterDropdown.css";
import { DownIcon, UpIcon, RighIcon } from "@/ImageRepo";

const CheckboxList = ({ schema }) => {
  const items = useSelector(schema.readVal);
  const dispatch = useDispatch();
  const [groupChecked, setGroupChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const includingAllCheckBox = schema.includingAllCheckBox ?? false;

  useEffect(() => {
    includingAllCheckBox && resetGroupChecked(items);
  }, []);

  const handleCheckboxChange = (e, item) => {
    let tempItems = structuredClone(items); // deep clone/copy (As when using redux-useSelector, there is chance readonly object is returned)
    let index = tempItems.findIndex(
      (x) => x[schema.uniqueKey] === item[schema.uniqueKey]
    );

    if (index > -1) {
      tempItems[index].isChecked = !tempItems[index].isChecked;
      dispatch(schema.setValue({ value: tempItems }));
    } else {
      alert("An Error occured while changing filters");
    }
    includingAllCheckBox && resetGroupChecked(tempItems);
  };
  const resetGroupChecked = (tempItems) => {
    let checked = false;
    let unChecked = false;
    tempItems.forEach((x) => {
      if (x.isChecked) checked = true;
      if (!x.isChecked) unChecked = true;
    });
    setGroupChecked(checked && !unChecked);
    setIndeterminate(checked && unChecked);
  };
  const handleGroupChanged = (e, item) => {
    let tempItems = structuredClone(items);
    tempItems.forEach((x) => (x.isChecked = item));
    dispatch(schema.setValue({ value: tempItems }));
    setGroupChecked(item); // Update the state to force re-render
    setIndeterminate(false);
  };

  return (
    <>
      {/* <div>{schema.childLabel}</div> */}
      {includingAllCheckBox && (
        <div key={"all"} className="filter-checkbox-list-items">
          <FormControlLabel
            label="All"
            control={
              <ShepCheckbox
                sx={{
                  paddingLeft: "1.5rem",
                }}
                checked={groupChecked}
                indeterminate={indeterminate}
                onChange={handleGroupChanged}
              />
            }
          />
        </div>
      )}
      {items?.map((item, i) => (
        <div key={i} className="filter-checkbox-list-items">
          <FormControlLabel
            key={i}
            label={item[schema.labelKey]}
            className="filter-checkbox-list-label"
            control={
              <ShepCheckbox
                checked={item.isChecked}
                onChange={(e) => handleCheckboxChange(e, item)}
              />
            }
          />
        </div>
      ))}
    </>
  );
};

const SearchableCheckboxList = ({ schema }) => {
  const items = useSelector(schema.readVal);
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [allChecked, setAllChecked] = useState(false);

  useEffect(() => {
    setAllChecked(items.every((item) => item.isChecked));
  }, [items]);

  const handleCheckboxChange = (e, item) => {
    let tempItems = structuredClone(items); // deep clone/copy (As when using redux-useSelector, there is chance readonly object is returned)
    let index = tempItems.findIndex(
      (x) => x.ConnectionId === item.ConnectionId
    );

    if (index > -1) {
      tempItems[index].isChecked = !tempItems[index].isChecked;
      dispatch(schema.setValue({ value: tempItems }));
      setAllChecked(tempItems.every((item) => item.isChecked)); // Update allChecked state
    } else {
      alert("An Error occurred while changing filters");
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleAllCheckboxChange = () => {
    let tempItems = structuredClone(items);
    const newCheckedState = !allChecked;
    tempItems.forEach((item) => (item.isChecked = newCheckedState));
    dispatch(schema.setValue({ value: tempItems }));
    setAllChecked(newCheckedState); // Update the state to force re-render
  };

  const filteredItems = items.filter((item) =>
    item.DisplayName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <div key={"All"} className="filter-checkbox-list-items">
        <FormControlLabel
          label="All"
          className="filter-checkbox-list-label"
          control={
            <ShepCheckbox
              checked={allChecked}
              onChange={handleAllCheckboxChange}
            />
          }
        />
      </div>
      <TextField
        value={searchTerm}
        onChange={handleSearchChange}
        sx={{
          "&.MuiFormControl-root .MuiInputBase-root": {
            borderRadius: "8px",
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      {items?.map((item, i) => (
        <div
          key={i}
          className="filter-checkbox-list-items"
          style={{ display: filteredItems.includes(item) ? "block" : "none" }}
        >
          <FormControlLabel
            key={i}
            label={item.DisplayName}
            className="filter-checkbox-list-label"
            control={
              <ShepCheckbox
                checked={!!item.isChecked} // Ensure checked is always a boolean
                onChange={(e) => handleCheckboxChange(e, item)}
              />
            }
          />
        </div>
      ))}
    </>
  );
};

const DateFilter = ({ schema }) => {
  const dateType = useSelector(schema.readVal);
  const dispatch = useDispatch();
  const currentDateFilter = useSelector(schema.readDateVal);
  const [dates, setDates] = useState([
    currentDateFilter.startDate,
    currentDateFilter.endDate,
  ]);

  useEffect(() => {
    //delay when making first date selection
    debounceSetDateToFilter(dates[0], dates[1]);
  }, [dates[0]]);

  useEffect(() => {
    //cancel delay and trigger backend call immediately (flush) after second date selection
    debounceSetDateToFilter(dates[0], dates[1]);
    debounceSetDateToFilter.flush();
  }, [dates[1]]);

  const setDateToFilter = (startDate, endDate) => {
    if (startDate == null || endDate == null) return;
    dispatch(
      schema.setDateValue({ value: { startDate: startDate, endDate: endDate } })
    );
  };

  const debounceSetDateToFilter = useCallback(
    _.debounce(setDateToFilter, 2000),
    []
  );

  const handleCheckboxChange = (e, type) => {
    let tempDateType = structuredClone(dateType); // deep clone/copy
    let index = tempDateType.findIndex(
      (x) => x[schema.uniqueKey] === type[schema.uniqueKey]
    );

    if (index > -1) {
      tempDateType[index].isChecked = !tempDateType[index].isChecked;
      dispatch(schema.setValue({ value: tempDateType }));
    } else {
      alert("An Error occured while changing filters");
    }
  };
  return (
    <>
      <div>{schema.childLabel}</div>
      {dateType?.map((type) => (
        <FormControlLabel
          key={type.id}
          label={type.label}
          control={
            <ShepCheckbox
              checked={type.isChecked}
              onChange={(e) => handleCheckboxChange(e, type)}
            />
          }
        />
      ))}
      {dateType?.some((x) => x?.isChecked) ? (
        <>
          <div>
            <CustomDatePicker
              dates={[currentDateFilter.startDate, currentDateFilter.endDate]}
              setDates={setDates}
              onApply={() => {}}
            />
          </div>
        </>
      ) : (
        <></>
      )}
    </>
  );
};

const SimpleFilter = ({ schema, setIndexForNestedOption }) => {
  const value = useSelector(schema.readVal); // This should be a boolean value
  const dispatch = useDispatch();

  const handleCheckboxChange = (e, schema) => {
    setIndexForNestedOption(null); // Close any open: nested Filter Menu
    dispatch(schema.setValue({ value: e.target.checked }));
  };

  return (
    <>
      <FormControlLabel
        label={schema.label}
        control={
          <ShepCheckbox
            sx={{
              paddingLeft: "1.5rem",
            }}
            checked={value}
            onChange={(e) => handleCheckboxChange(e, schema)}
          />
        }
      />
    </>
  );
};

const CustomFilterDropdown = ({ schemas }) => {
  const [open, setOpen] = useState(false);
  const [indexForNestedOption, setIndexForNestedOption] = useState(null);

  const handleDropdownToggle = () => {
    setOpen(!open);
    setIndexForNestedOption(null);
  };

  const handleFilterClose = () => {
    setOpen(false);
    setIndexForNestedOption(null);
  };

  const handleMenuItemClick = (schema, i) => {
    setIndexForNestedOption(null);
    if (schema.cascading) {
      setIndexForNestedOption(i);
    }
  };

  const renderComponentByType = (schema) => {
    if (schema.type === FilterDropDownType.CheckboxList) {
      return <CheckboxList schema={schema} />;
    } else if (schema.type === FilterDropDownType.SearchableCheckboxList) {
      return <SearchableCheckboxList schema={schema} />;
    } else if (schema.type === FilterDropDownType.Date) {
      return <DateFilter schema={schema} />;
    }
  };

  return (
    <>
      <ClickAwayListener onClickAway={handleFilterClose}>
        <Box className="filter-container">
          <Box
            onClick={handleDropdownToggle}
            className={`filter-input ${open ? "filter-open" : ""}`}
          >
            <FilterIcon width={16} height={16} />
            <div className="filter-main-label">All</div>
            <div className="filter-main-arrow-icon">
              {open ? (
                <img src={UpIcon} width={16} height={16} />
              ) : (
                <img src={DownIcon} width={16} height={16} />
              )}
            </div>
          </Box>
          {open ? (
            <div className="filter-options">
              {schemas.map((schema, i) => (
                <div className="filter-options-container" key={i}>
                  {!schema.cascading ? (
                    // This are non nested -> simple checkbox schemas
                    <>
                      <SimpleFilter
                        schema={schema}
                        setIndexForNestedOption={setIndexForNestedOption}
                      />
                    </>
                  ) : (
                    // This schemas are nested -> so load as button, so upon click can render appropriate UI based on Type.
                    <div
                      onClick={() => handleMenuItemClick(schema, i)}
                      className="filter-options-items"
                    >
                      <Button>{schema.label}</Button>
                      <img src={RighIcon} width={16} height={16} />
                    </div>
                  )}
                  {schema.cascading && i === indexForNestedOption ? (
                    <Box className="filter-nested-options-level-1">
                      {/* Render nested UI based on the schema type */}
                      {renderComponentByType(schema)}
                    </Box>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <></>
          )}
        </Box>
      </ClickAwayListener>
    </>
  );
};

export default CustomFilterDropdown;
